import { API_CALL } from 'actions/actionConstants'
import { ENROL_LOYALTY_EARN_DECISION } from 'actions/apiActionConstants'
import type { DefinedAppAction } from 'actions/types/ActionTypes'
import fetchLoyaltyAccount from 'actions/loyalty/fetchLoyaltyAccount'
import requestLoyaltyEarnDecisionEnrolment from 'api/loyalty/requestLoyaltyEarnDecisionEnrolment'
import isValidLoyaltyRegion from 'api/lib/isValidLoyaltyRegion'
import calculateTierRemaining from 'loyalty/lib/calculateTierRemaining'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getLoyaltyTierThresholds from 'loyalty/selectors/getLoyaltyTierThresholds'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import type { ApiActionResult } from 'middlewares/apiMiddleware'
import type { ThunkAction } from 'redux-thunk'

type Result = ApiActionResult<Array<App.LoyaltyEarnDecision>>

function enrolLoyaltyEarnDecision(decisionCatalogId: string): ThunkAction<Promise<Result | undefined>, App.State, undefined, DefinedAppAction> {
  return async(dispatch, getState) => {
    const state = getState()

    const customerId = state.auth.account.memberId
    const account = state.loyalty.account.data
    const enrolment = state.loyalty.earnDecisionEnrolment?.[decisionCatalogId]

    if (
      !customerId ||
      !getHasLoyaltyAccount(state) ||
      state.loyalty.account.fetching ||
      state.loyalty.account.error ||
      enrolment?.fetching ||
      enrolment?.data) return

    const region = state.geo.currentRegionCode
    // Account reads leave these at 0 for a new member. When tier thresholds are already loaded,
    // use the same next-tier fallback as tier progress. Otherwise keep the account zeros.
    const { targetCredits, targetNights } = calculateTierRemaining(account!, 'upgrade', getLoyaltyTierThresholds(state))

    const metadata = {
      tier: account!.tier,
      points: account!.points,
      statusCredits: account!.statusCredits,
      numberOfNights: account!.numberOfNights,
      numberOfNightsForTierUpgrade: targetNights,
      totalCreditsForTierUpgrade: targetCredits,
      luxPlusMember: getCanRedeemLuxPlusBenefits(state),
      region: isValidLoyaltyRegion(region) ? region : undefined,
      platform: 'web',
    }

    return new Promise<Result>(resolve => {
      dispatch({
        type: API_CALL,
        api: ENROL_LOYALTY_EARN_DECISION,
        decisionCatalogId,
        request: () => requestLoyaltyEarnDecisionEnrolment({
          op: 'enrol',
          customerId,
          decisionCatalogId,
          metadata,
        }),
        onComplete: async(result: Result) => {
          if (result.status === 'success') {
            // Failure to refresh must not turn an already committed enrolment into a failure.
            await dispatch(fetchLoyaltyAccount(undefined, { force: true }))
          }
          resolve(result)
        },
      })
    })
  }
}

export default enrolLoyaltyEarnDecision
